// MobileProfilePicker — a escolha do perfil no CELULAR (decisão do dono, 10/10/2026:
// "pergunta 1 vez se houver mais de um").
//
// Três peças, um só componente de escolha:
//  - a FOLHA "Quem está assistindo?" (`mode: 'choose'`): abre sozinha UMA vez quando o
//    bootstrap não pode decidir (2+ perfis sem "último usado" neste aparelho, um só
//    Infantil, um só com PIN). A escolha fica lembrada (`lastProfileIdByAccount`);
//  - a mesma folha como "Trocar perfil" (`mode: 'switch'`), aberta pelo CHIP do perfil
//    ativo no cabeçalho — o perfil ativo está sempre visível e trocável;
//  - o AVISO "Escolha um perfil para continuar" quando não há perfil ativo: sem ele, o
//    escopo do local-db não existe com conta (scope.ts) e nada se grava em `pf:_main`.
//
// Com o pull dos perfis da conta OK, lista só os perfis DA CONTA (o perfil só-local,
// escolhido, viraria duplicado na conta). Com o pull FALHO, lista os perfis que estão NESTE
// aparelho (não dá pra separar os da conta sem o pull) junto com o "Tentar novamente".
// Perfil com PIN pede o PIN (MobilePinEntry, o mesmo da aba Perfil). Touch puro, sem
// Norigin (regra da frente mobile).

import { useTranslation } from '@app-iptv/i18n'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { getProfileById, type Profile, setProfileHasPin } from '../local-db'
import { loadAccountProfiles } from '../profiles-load'
import { accountKey as accountKeyOf, useSessionStore } from '../store'
import { confirmPinRecoverRemote, requestPinRecoverRemote, syncPull, verifyProfilePinRemote } from '../sync'

import { MobilePinEntry } from './MobilePinEntry'
import styles from './MobileProfilePicker.module.css'

/** Os perfis que a folha oferece: os da conta (quando o pull disse quais são). */
export function pickerChoices<T extends { id: string }>(
  profiles: ReadonlyArray<T>,
  remoteIds: ReadonlySet<string> | null,
): T[] {
  return remoteIds ? profiles.filter((p) => remoteIds.has(p.id)) : [...profiles]
}

export interface MobileProfilePickerProps {
  mode: 'choose' | 'switch'
  onClose: () => void
  /** Conta sem perfil: leva à aba Perfil, onde se cria um. */
  onOpenProfile: () => void
}

export function MobileProfilePicker({ mode, onClose, onOpenProfile }: MobileProfilePickerProps): JSX.Element {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const account = useSessionStore((s) => accountKeyOf(s))
  const activeProfileId = useSessionStore((s) => s.activeProfileId)
  const setActiveProfile = useSessionStore((s) => s.setActiveProfile)

  const profilesQ = useQuery({
    queryKey: ['mobile-profiles', account],
    queryFn: () => loadAccountProfiles(account as string),
    enabled: account !== null,
    staleTime: 60 * 1000,
  })
  const choices = profilesQ.data ? pickerChoices(profilesQ.data.profiles, profilesQ.data.remoteIds) : []
  // A carga falhou (a query deu erro, ou o pull dos perfis da conta não veio). Sem isto a
  // folha ficava em "Carregando…" pra sempre, ou oferecia "+ Novo perfil" a quem só não
  // conseguiu ver os perfis da conta (o duplicado de novo). Gate ux-tv, rodada 3.
  // Também é falha a conta TER perfis e a lista do aparelho vir vazia (a leitura local
  // falhou): oferecer "+ Novo perfil" seria o duplicado (rodada 6).
  const accountHasProfiles = (profilesQ.data?.remoteIds?.size ?? 0) > 0
  const failed =
    profilesQ.isError || profilesQ.data?.pull === 'failed' || (accountHasProfiles && choices.length === 0)
  const retry = (
    <div className={styles.actions}>
      <p className={styles.hint}>{t('empty.loadFailedTitle')}</p>
      <button type="button" className={styles.primary} data-action="picker-retry" onClick={() => void profilesQ.refetch()}>
        {t('common.retry')}
      </button>
    </div>
  )

  const [verifyFor, setVerifyFor] = useState<Profile | null>(null)
  const [pinError, setPinError] = useState<string | null>(null)
  const [pinReset, setPinReset] = useState(0)
  const [pinBusy, setPinBusy] = useState(false)

  const enter = (p: Pick<Profile, 'id' | 'kind'>): void => {
    setActiveProfile({ id: p.id, kind: p.kind })
    // O histórico do perfil escolhido ("continuar de onde parou" vindo da TV).
    void syncPull()
    void queryClient.invalidateQueries()
    onClose()
  }

  const choose = (p: Profile): void => {
    if (p.hasPin) {
      setPinError(null)
      setPinBusy(false)
      setVerifyFor(p)
      return
    }
    enter(p)
  }

  const submitPin = async (pin: string): Promise<void> => {
    const target = verifyFor
    if (!target) return
    setPinBusy(true)
    setPinError(null)
    const res = await verifyProfilePinRemote(target.id, pin)
    setPinBusy(false)
    if (res.status === 'ok' || res.status === 'no_pin') {
      if (res.status === 'no_pin') await setProfileHasPin(target.id, false)
      setVerifyFor(null)
      enter(target)
      return
    }
    setPinError(res.status === 'incorrect' ? t('pin.incorrect') : res.status === 'locked' ? t('pin.locked') : t('pin.networkError'))
    setPinReset((n) => n + 1)
  }

  if (verifyFor) {
    return (
      <MobilePinEntry
        purpose="verify"
        profileName={verifyFor.name}
        onSubmit={(pin) => void submitPin(pin)}
        onCancel={() => setVerifyFor(null)}
        error={pinError}
        resetSignal={pinReset}
        busy={pinBusy}
        onRequestRecover={() => requestPinRecoverRemote(verifyFor.id)}
        onConfirmRecover={(code) => confirmPinRecoverRemote(verifyFor.id, code)}
        onRecovered={() => {
          void setProfileHasPin(verifyFor.id, false)
          const target = verifyFor
          setVerifyFor(null)
          enter(target)
        }}
      />
    )
  }

  return (
    <div className={styles.backdrop} data-sheet="profile-picker" data-mode={mode} onClick={onClose}>
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="profile-picker-title" className={styles.title}>
          {mode === 'choose' ? t('profiles.who') : t('mobileProfile.switchTitle')}
        </h2>
        {mode === 'choose' && <p className={styles.hint}>{t('mobileProfile.pickerHint')}</p>}
        {failed && choices.length === 0 ? (
          retry
        ) : profilesQ.data === undefined ? (
          <p className={styles.hint}>{t('mobileProfile.loading')}</p>
        ) : choices.length === 0 ? (
          <div className={styles.actions}>
            <button type="button" className={styles.primary} data-action="picker-new-profile" onClick={onOpenProfile}>
              + {t('profiles.new')}
            </button>
          </div>
        ) : (
          <>
          {failed && retry}
          <ul className={styles.grid}>
            {choices.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={styles.choice}
                  data-profile={p.id}
                  data-active={p.id === activeProfileId ? 'true' : 'false'}
                  aria-current={p.id === activeProfileId ? 'true' : undefined}
                  onClick={() => choose(p)}
                  aria-label={p.hasPin ? `${p.name}, ${t('pin.protectedAria')}` : p.name}
                >
                  <span className={styles.avatar} data-kids={p.kind === 'kids' ? 'true' : 'false'} aria-hidden="true">
                    {p.avatar}
                  </span>
                  <span className={styles.name}>{p.name}</span>
                  {p.kind === 'kids' && <span className={styles.tagKids}>{t('profiles.kids')}</span>}
                  {p.hasPin && <span className={styles.tag}>{t('mobileProfile.pickerPinTag')}</span>}
                </button>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>
    </div>
  )
}

/** O chip do perfil ativo no cabeçalho: avatar sempre visível; o toque troca o perfil. */
export function MobileActiveProfileChip({ onPress }: { onPress: () => void }): JSX.Element | null {
  const { t } = useTranslation()
  const activeProfileId = useSessionStore((s) => s.activeProfileId)
  const profileQ = useQuery({
    queryKey: ['mobile-active-profile', activeProfileId],
    queryFn: async () => (await getProfileById(activeProfileId as string)) ?? null,
    enabled: activeProfileId !== null,
    staleTime: 60 * 1000,
  })
  if (!activeProfileId) return null
  const p = profileQ.data
  return (
    <button
      type="button"
      className={styles.chip}
      data-action="active-profile"
      aria-label={t('mobileProfile.activeChipAria', { name: p?.name ?? '' })}
      onClick={onPress}
    >
      <span className={styles.chipAvatar} aria-hidden="true">
        {p?.avatar ?? '👤'}
      </span>
    </button>
  )
}

/** O aviso de "sem perfil ativo": conduz à escolha (a mesma folha). */
export function MobileNoActiveProfile({ onChoose }: { onChoose: () => void }): JSX.Element {
  const { t } = useTranslation()
  return (
    <section className={styles.noActive} data-state="no-active-profile" role="status">
      <h2 className={styles.noActiveTitle}>{t('mobileProfile.noActiveTitle')}</h2>
      <p className={styles.noActiveBody}>{t('mobileProfile.noActiveBody')}</p>
      <button type="button" className={styles.primary} data-action="choose-profile" onClick={onChoose}>
        {t('mobileProfile.noActiveCta')}
      </button>
    </section>
  )
}

/**
 * O celular entrou numa conta e a fonte dela não pôde ser lida (ex.: 401 da conta
 * anterior + hidratação da nova falhando): sem isto a Home caía em "Nada por aqui ainda",
 * sem explicação (gate ux-tv, rodada 3). Reusa a copy de "não foi possível carregar".
 */
export function MobileAccountLoadFailed({ onRetry }: { onRetry: () => void }): JSX.Element {
  const { t } = useTranslation()
  return (
    <section className={styles.noActive} data-state="account-load-failed" role="alert">
      <h2 className={styles.noActiveTitle}>{t('empty.loadFailedTitle')}</h2>
      <p className={styles.noActiveBody}>{t('empty.loadFailedDesc')}</p>
      <button type="button" className={styles.primary} data-action="account-retry" onClick={onRetry}>
        {t('common.retry')}
      </button>
    </section>
  )
}
